import { useEffect } from 'react'

/**
 * Warn before the PAGE goes away with typed work on screen (#274's last
 * unwired path, #349 A11).
 *
 * `useUnsavedGuard` covers the in-app closes and `useBrowserBackSentinel`
 * covers Back. Neither can see a reload, a tab close, or a link that leaves the
 * app — the app has no router, so "navigate away" means exactly those. Only the
 * browser can intervene there, and only through `beforeunload`.
 *
 * The browser shows its own generic wording and ignores any message we set; the
 * contract is simply "call preventDefault to ask". Registered only while dirty,
 * because a page that always asks is a page people learn to click through — and
 * on some browsers an always-present handler also disables the back/forward
 * cache for the whole session.
 */
export function useBeforeUnloadGuard(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Legacy browsers keyed off the return value rather than preventDefault.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])
}
